import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';

import { ClickAwayListener, FormHelperText, Grow, InputAdornment, Paper, Popper, Slider } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import GoalCheckInCard from 'components/GoalCheckInCard/GoalCheckInCard';
import SkeletonLoader from 'components/SkeletonLoader';
import { StyledAvatar } from 'components/avatars';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { differenceInCalendarDays, endOfQuarter } from 'date-fns';
import endOfDay from 'date-fns/endOfDay';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import Select from 'react-select';
import { AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Area, ReferenceLine } from 'recharts';
import DueDateField from 'screens/common/sidepanel/DetailSection/DueDateField';
import { RestrictedAccessContainerWithTooltip } from 'screens/common/sidepanel/DetailSection/GoalDetailSection';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import {
  hideNumberInputArrowsCSS,
  SpaceBetweenContainer,
  StyledGrid,
  StyledTextField,
  StyledTypography,
  VerticalFlexContainer,
  VerticallyAlignedFlexContainer,
  Divider1,
  StyledBox,
} from 'styles/common';

import {
  GOAL_CLOSED_OPTIONS,
  GOAL_IN_PROGRESS_OPTIONS,
  GOAL_MEASUREMENT_TYPES,
  GOAL_PROGRESS_ENTITIES,
  GOAL_PROGRESS_OPTIONS_MAP,
} from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import {
  convertDateToString3,
  getDateFromEpoch,
  indianFormattedNumber,
  foreignFormattedNumber,
  getCurrencyDetails,
  findAndReplaceString,
  generateKey,
} from 'constants/helper';
import { KH_LINKS } from 'constants/knowledgeHubLinks';
import { GOAL_EVENTS1, sendGoalEvent } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { getGoalProgress } from 'services/helpers/goal';
import { parseProgressGraphRes } from 'services/helpers/task';
import { showErrorSnackbar } from 'services/snackbar';
import { getGoalGraphProgress } from 'services/task';

import TooltipInfo from 'uiToolkit/TooltipInfo';
import { Typography8, Typography4, Typography13 } from 'uiToolkit/Typography';

import { Heading } from '../DetailSection/GoalDetailSection';
import { HeaderText, IndianNumberFormatCustom, ForeignNumberFormatCustom } from './GoalHelperComponents';

const StyledThumb = styled.span`
  width: 1rem;
  padding: 0 0.25rem;
  display: inline-block;
  border-radius: 0.25rem;
  border: 1px solid ${(props) => props.borderColor};
`;

const ProgressSlider = withStyles({
  root: {
    color: (props) => props.color,
    height: 10,
    minWidth: '60%',
    width: '90%',
  },
  thumb: {
    height: 20,
    width: 30,
    backgroundColor: '#fff',
    border: '2px solid currentColor',
    borderRadius: '20%',
    marginTop: -5,
    marginLeft: -12,
    '&:focus, &:hover, &$active': {
      boxShadow: 'inherit',
    },
  },
  active: {},
  valueLabel: {
    left: 'calc(-50% + 8px)',
  },
  track: {
    height: 10,
    borderRadius: 5,
  },
  rail: {
    backgroundColor: '#DADADA',
    height: 10,
    borderRadius: 5,
  },
  mark: {
    display: 'none',
  },
})(Slider);

export const getDefaultSelectedOption = (progressLevel) => {
  const filteredOptions = [...GOAL_IN_PROGRESS_OPTIONS, ...GOAL_CLOSED_OPTIONS].filter(
    ({ value }) => value === progressLevel
  );
  return filteredOptions.length === 1 ? filteredOptions[0] : null;
};

export const getMeasureUnitDetails = (unit, organizationCurrency = null) => {
  if (unit === null || unit === '' || unit === undefined) {
    unit = 'PERCENTAGE';
  }
  const matchedType = GOAL_MEASUREMENT_TYPES.filter((type) => type.code === unit);
  // currency unit
  if (matchedType[0]?.code === 'CURRENCY') {
    return { ...getCurrencyDetails(organizationCurrency) };
  }
  // non-currency unit
  return matchedType[0];
};

export const numberFormatter = (number, orgCurrency) =>
  orgCurrency && orgCurrency === 'INR' ? indianFormattedNumber(number) : foreignFormattedNumber(number);

export const StatusSelectDropdown = ({
  progressLevel,
  goalClosureStatus,
  updateTask,
  goalDetails,
  pageName,
  customStyles = null,
  isCheckInCard = false,
  setCheckInCardStatus = () => {},
}) => (
  <Select
    menuShouldScrollIntoView={false}
    classNamePrefix="react-select"
    label="Single select"
    options={[
      {
        label: 'In Progress',
        options: GOAL_IN_PROGRESS_OPTIONS,
      },
      {
        label: 'Closure',
        options: GOAL_CLOSED_OPTIONS,
      },
    ]}
    defaultValue={
      progressLevel === 'CLOSED' ? getDefaultSelectedOption(goalClosureStatus) : getDefaultSelectedOption(progressLevel)
    }
    value={
      progressLevel === 'CLOSED' ? getDefaultSelectedOption(goalClosureStatus) : getDefaultSelectedOption(progressLevel)
    }
    onChange={(option) => {
      if (isCheckInCard) {
        setCheckInCardStatus(option);
      } else {
        if (option.progressLevel === 'CLOSED')
          updateTask(
            {
              ...goalDetails,
              goalProgressLevel: option.progressLevel,
              goalClosureStatus: option.value,
            },
            false
          );
        else updateTask({ ...goalDetails, goalProgressLevel: option.progressLevel }, false);

        sendGoalEvent({
          page: { pageName, flow: 'Activity' },
          eventName: GOAL_EVENTS1.status,
          oldValue: {
            progressLevel,
            goalClosureStatus,
          },
          currentValue: {
            progressLevel: option.progressLevel,
            goalClosureStatus: option.value,
          },
          referrer: 'SidePanel',
        });
      }
    }}
    styles={customStyles ?? colourStyles}
    theme={(theme, data) => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary: 'lightGrey',
      },
    })}
  />
);

const PercentageAchievedValue = ({
  achieved,
  hasError,
  achievedRef,
  submitAchievedValue,
  setHasError,
  setAchieved,
  handleKeyDown,
  taskDetails,
  isPrefix,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch();

  if (!isEditing && !hasError) {
    return (
      <StyledNumberTextField
        style={{ marginLeft: '-12px', width: '50%' }}
        value={`${achieved}%`}
        variant="outlined"
        margin="none"
        size="small"
        // disabled={true}
        onClick={() => setIsEditing(true)}
      />
    );
  }

  return (
    <ClickAwayListener
      onClickAway={() => {
        setIsEditing(false);
      }}
    >
      <StyledNumberTextField
        style={{ marginLeft: '-12px', width: '50%' }}
        // fullWidth
        inputRef={achievedRef}
        error={hasError}
        value={achieved}
        variant="outlined"
        margin="none"
        size="small"
        type="number"
        onBlur={submitAchievedValue}
        onChange={({ target }) => {
          if (target?.value?.trim()?.length) {
            // trim.length makes sure that it at least has 1 character
            setHasError(false);
          } else {
            setHasError(true);
          }
          const regex = /^[0-9-\b]+$/g;
          if (!regex.test(target.value)) {
            if (!target.value) {
              setAchieved(target.value);
            } else if (target.value.includes('.')) {
              setAchieved(target.value);
            } else {
              dispatch(showErrorSnackbar('Only numbers are allowed'));
              return;
            }
          }
          setAchieved(target.value);
        }}
        onKeyDown={(e) => {
          handleKeyDown(e, () => setIsEditing(false));
        }}
        disabled={!!taskDetails.source}
        autoFocus
        $isPrefix={isPrefix}
      />
    </ClickAwayListener>
  );
};

const ProgressBar = ({
  achievedValue = 0,
  targetValue = 0,
  initialValue = 0,
  unit,
  taskDetails,
  updateTask,
  progressLevel,
  goalClosureStatus = 'ACHIEVED',
  disabled = false,
  pageName,
}) => {
  const [achieved, setAchieved] = useState(achievedValue);
  const [previousAchievedValue, setPreviousAchievedValue] = useState(achievedValue);
  const [target, setTarget] = useState(targetValue);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  // There is a new requirement of not sending completion date for goal, making sure that is met
  const goalDetails = { ...taskDetails, completionDate: null };

  const [hasError, setHasError] = useState(false);

  const achievedRef = useRef(null);

  let orgCurrency = loggedInEmployee?.organization?.currency ?? null;
  if (!orgCurrency) {
    orgCurrency = 'INR';
  }
  const { symbol: unitSymbol, isPrefix } = useMemo(() => getMeasureUnitDetails(unit, orgCurrency), [orgCurrency, unit]);

  /*
    Slider doesn't support target < initial.In such case, doing following to simulate
    same effect.
    1. Swapping slider min and slider max, makes slider work.
    2. positioning of slider thumb is decided by startValue + targetValue - achievedValue.
    - e.g. for Start = -200 and target = -500 and achieved = -300, the value for this swapped slider
      should be Start + target - achieved = (-200) + (-500) - (-300) = -700 + 300 = -400.
      this should move the thumb to proper position to simulate negative slider.
  */
  const range = initialValue - targetValue;
  const isInverse = initialValue > targetValue;
  const handleKeyDown = (ev, setIsEditing = () => {}) => {
    if (ev.key === 'Enter') {
      submitAchievedValue({ onSuccess: setIsEditing });
    }
  };

  const submitAchievedValue = async ({ onSuccess = () => {} }) => {
    // check if achieved value is valid
    // 1. if achieved value is greater than initialValue and less than targetValue.
    // 2. if achieved value is a number.

    if (!hasError) {
      if (achieved !== achievedValue) {
        sendGoalEvent({
          page: { pageName, flow: 'Activity' },
          eventName: 'UpdatedProgressBar',
          field: 'actual',
          referrer: 'SidePanel',
        });
        await updateTask({ ...goalDetails, actual: achieved }, false);
        // achievedRef.current && achievedRef.current.blur();
        if (onSuccess && typeof onSuccess === 'function') {
          onSuccess();
        }
      }
    } else {
      achievedRef.current && achievedRef.current.focus();
    }
  };

  useEffect(() => {
    setAchieved(achievedValue);
  }, [achievedValue]);

  useEffect(() => {
    // setTarget(targetValue);
  }, [targetValue]);

  const [checkInCardIsVisible, setCheckInCardIsVisible] = useState(false);
  const [updatedActualValue, setUpdatedActualValue] = useState(0);

  const onCheckInCancelHandler = () => {
    setCheckInCardIsVisible(false);
    setAchieved(previousAchievedValue);
  };

  useEffect(() => {
    setUpdatedActualValue(achieved);
  }, [achieved]);

  const anchorRef = useRef(null);

  return (
    <ProgressBarContainer>
      <StyledBox
        style={{ border: '1px solid #eaeaea', borderRadius: '4px', width: '100%', padding: '16px' }}
        bottomGutter={5}
      >
        <StyledGrid container bottomGutter={1}>
          <StyledGrid item xs={4}>
            <Heading
              style={{
                overflowWrap: 'break-word',
              }}
            >
              Achieved {goalDetails?.measureType}
            </Heading>
          </StyledGrid>
          <StyledGrid item xs={1} />
          <StyledGrid item xs={4}>
            <Heading leftGutter={1.5}>Status</Heading>
          </StyledGrid>
          <StyledGrid item xs={3}>
            <Heading>Completed</Heading>
          </StyledGrid>
        </StyledGrid>

        <StyledGrid container>
          <StyledGrid item xs={4}>
            <VerticallyAlignedFlexContainer>
              {unitSymbol === '%' ? (
                <PercentageAchievedValue
                  achieved={achieved}
                  hasError={hasError}
                  achievedRef={achievedRef}
                  submitAchievedValue={submitAchievedValue}
                  setHasError={setHasError}
                  setAchieved={setAchieved}
                  handleKeyDown={handleKeyDown}
                  taskDetails={taskDetails}
                  isPrefix={isPrefix}
                />
              ) : (
                <>
                  <StyledNumberTextField
                    style={{ marginLeft: '-12px' }}
                    // fullWidth
                    inputRef={achievedRef}
                    error={hasError}
                    value={achieved}
                    variant="outlined"
                    margin="none"
                    size="small"
                    onBlur={submitAchievedValue}
                    onChange={({ target }) => {
                      if (target?.value?.trim()?.length) {
                        // trim.length makes sure that it at least has 1 character
                        setHasError(false);
                        setAchieved(target.value);
                      } else {
                        setAchieved(target.value);
                        setHasError(true);
                      }
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={!!taskDetails.source}
                    InputProps={{
                      inputComponent: orgCurrency === 'INR' ? IndianNumberFormatCustom : ForeignNumberFormatCustom,
                      startAdornment: isPrefix && unitSymbol !== '#' && (
                        <StyledInputAdornment position="start">{unitSymbol}</StyledInputAdornment>
                      ),
                      // endAdornment: !isPrefix && unitSymbol !== '#' && (
                      //   <InputAdornment style={{ marginRight: '0.5rem' }} position="end">
                      //     {unitSymbol}
                      //   </InputAdornment>
                      // )
                    }}
                    $isPrefix={isPrefix}
                  />
                  {!isPrefix && unitSymbol !== '#' && (
                    <StyledInputAdornment style={{ marginRight: '0.5rem' }} position="end">
                      {unitSymbol}
                    </StyledInputAdornment>
                  )}
                </>
              )}
            </VerticallyAlignedFlexContainer>
            {hasError && (
              <FormHelperText style={{ color: theme.palette.text.red }}>This is a mandatory field</FormHelperText>
            )}
          </StyledGrid>
          <StyledGrid item xs={1} />
          <StyledGrid item xs={4}>
            <StatusSelectDropdown
              progressLevel={progressLevel}
              goalClosureStatus={goalClosureStatus}
              updateTask={updateTask}
              goalDetails={goalDetails}
              pageName={pageName}
            />
          </StyledGrid>
          <StyledGrid item xs={3}>
            <Typography13>
              <StyledNumberTextField
                style={{ marginLeft: '-12px' }}
                value={`${getGoalProgress({
                  defaultDisplayValue: 0,
                  startValue: goalDetails.startingProgressValue,
                  achievedValue: goalDetails.actual,
                  targetValue: goalDetails.target,
                })}%`}
                variant="outlined"
                margin="none"
                size="small"
                disabled
              />
            </Typography13>
          </StyledGrid>
        </StyledGrid>
      </StyledBox>

      {
        <ClickAwayListener onClickAway={checkInCardIsVisible ? onCheckInCancelHandler : () => {}}>
          <div style={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center' }}>
            <ProgressSlider
              ref={anchorRef}
              min={isInverse ? targetValue : initialValue}
              max={isInverse ? initialValue : targetValue}
              step={isInverse ? -1 : 1}
              marks={[
                {
                  value: isInverse ? targetValue : initialValue,
                  label: isPrefix
                    ? `${unitSymbol}${numberFormatter(initialValue, orgCurrency)}`
                    : unitSymbol === '#' // for symbol === "#" not displaying the unit symbol
                      ? `${numberFormatter(initialValue, orgCurrency)}`
                      : `${numberFormatter(initialValue, orgCurrency)}${unitSymbol}`,
                },
                {
                  value: isInverse ? initialValue : targetValue,
                  label: isPrefix
                    ? `${unitSymbol}${numberFormatter(targetValue, orgCurrency)}`
                    : unitSymbol === '#'
                      ? `${numberFormatter(targetValue, orgCurrency)}`
                      : `${numberFormatter(targetValue, orgCurrency)}${unitSymbol}`,
                },
              ]}
              color={GOAL_PROGRESS_OPTIONS_MAP[progressLevel]?.color}
              valueLabelDisplay="off"
              onMouseDown={() => {
                setCheckInCardIsVisible(true);
              }}
              onChange={(_, value) => {
                if (isInverse) {
                  setPreviousAchievedValue(achievedValue);
                  setAchieved(targetValue + initialValue - value);
                } else {
                  setPreviousAchievedValue(achievedValue);
                  setAchieved(value);
                }
              }}
              onChangeCommitted={(_, value) => {
                setCheckInCardIsVisible(true);
                setUpdatedActualValue(isInverse ? targetValue + initialValue - value : value);
              }}
              value={isInverse ? targetValue + initialValue - achieved : achieved}
              aria-label="progress slider"
            />
            <Popper
              style={{ zIndex: 5000 }}
              anchorEl={anchorRef.current}
              open={checkInCardIsVisible}
              placement="bottom"
              disablePortal
              modifiers={{
                preventOverflow: {
                  enabled: true,
                  boundariesElement: 'window',
                },
              }}
            >
              {({ TransitionProps, placement }) => (
                <Grow
                  {...TransitionProps}
                  style={{
                    transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                  }}
                >
                  <GoalCheckInCard
                    updatedActualValue={updatedActualValue}
                    updateTask={updateTask}
                    goalDetails={goalDetails}
                    onCheckInCancelHandler={onCheckInCancelHandler}
                    setCheckInCardIsVisible={setCheckInCardIsVisible}
                    achieved={achieved}
                    setAchieved={setAchieved}
                    progressLevel={progressLevel}
                    goalClosureStatus={goalClosureStatus}
                    pageName={pageName}
                    completedPercentage={`${getGoalProgress({
                      defaultDisplayValue: 0,
                      startValue: goalDetails.startingProgressValue,
                      achievedValue: goalDetails.actual,
                      targetValue: goalDetails.target,
                    })}%`}
                    measureType={goalDetails?.measureType}
                  />
                </Grow>
              )}
            </Popper>
          </div>
        </ClickAwayListener>
      }
    </ProgressBarContainer>
  );
};

const dot = (color = '#ccc') => ({
  alignItems: 'center',
  display: 'flex',

  ':before': {
    backgroundColor: color,
    borderRadius: 10,
    content: '" "',
    display: 'block',
    marginRight: 8,
    height: 10,
    width: 10,
  },
});

const colourStyles = {
  dropdownIndicator: (styles, data) => ({
    ...styles,
    color: 'transparent',
    padding: '4px',
  }),
  indicatorSeparator: (styles, data) => ({
    ...styles,
    backgroundColor: 'transparent',
  }),
  control: (styles, data) => {
    // const color = data.getValue()[0].color;
    const color = '#ccc';
    return {
      ...styles,
      backgroundColor: 'white',
      width: '15ch',
      minHeight: '30px',
      boxShadow: 'none',
      borderColor: 'transparent',
      ':hover': {
        ...styles[':hover'],
        borderColor: color,
        '& .react-select__dropdown-indicator': { color },
        '& .react-select__indicator-separator': { backgroundColor: color },
      },
      ':focus': {
        ...styles[':focus'],
        borderColor: color,
        '& .react-select__dropdown-indicator': { color },
        '& .react-select__indicator-separator': { backgroundColor: color },
      },
      ':active': {
        ...styles[':active'],
        '& .react-select__dropdown-indicator': { color },
        '& .react-select__indicator-separator': { backgroundColor: color },
      },
    };
  },
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    const hoverColor = '#F9F9F9';
    const selectedColor = '#F0F0F0';
    return {
      ...styles,
      backgroundColor: isSelected ? selectedColor : isFocused ? hoverColor : null,
      color: data.color,
      cursor: isDisabled ? 'not-allowed' : 'default',

      ':active': {
        ...styles[':active'],
        backgroundColor: isSelected ? selectedColor : hoverColor,
      },
      ...dot(data.color),
    };
  },
  input: (styles, state) => styles,
  singleValue: (styles, { data }) => ({ ...styles, color: data.color }),
};

const GoalProgress = forwardRef(({ taskDetails, updateTask, getTaskData, disabled, reason = '', pageName }, ref) => {
  const {
    id: goalId,
    target,
    actual: achieved,
    startingProgressValue,
    goalProgressLevel,
    startDate,
    measure: measureUnit,
    goalClosureStatus,
  } = taskDetails;

  // if dueDate is not defined set due date as 3months away from start date.
  let { dueDate } = taskDetails;
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  if (dueDate === null || dueDate === undefined) {
    dueDate = endOfQuarter(startDate);
  }

  return (
    <div ref={ref}>
      <VerticallyAlignedFlexContainer>
        <HeaderText weight={600}>Progress</HeaderText>
        <TooltipInfo
          title={findAndReplaceString(
            KH_LINKS.GOAL_PROGRESS.description,
            `${modifyString(keyResultsName, StringModifier.SENTENCE_CASE)} progress`,
            'progress'
          )}
          linkUrl={KH_LINKS.GOAL_PROGRESS.url}
          leftGutter={1}
        />
      </VerticallyAlignedFlexContainer>
      <BarAndGraphContainer>
        <RestrictedAccessContainerWithTooltip reason={reason} disabled={disabled}>
          <ProgressBar
            taskDetails={taskDetails}
            updateTask={updateTask}
            targetValue={target}
            achievedValue={achieved}
            initialValue={startingProgressValue}
            unit={measureUnit}
            // progressLevel is used for color.
            progressLevel={goalProgressLevel}
            goalClosureStatus={goalClosureStatus}
            getTaskData={getTaskData}
            disabled={disabled}
            pageName={pageName} // used in tracking
          />
        </RestrictedAccessContainerWithTooltip>
        {/* <StyledGrid bottomGutter={1} container alignItems="center">
          <StyledTypography variant="caption" display="inline" color="textSecondary">
            Starting
          </StyledTypography>
          <NumberTextField
            type="number"
            defaultValue={startingProgressValue}
            variant="outlined"
            margin="none"
            size="dense"
            rightGutter={1}
            leftGutter={1}
            onBlur={e => {
              const target = e.target;
              const inputValue = Number(target.value || 0);
              if (inputValue !== startingProgressValue) {
                const newTask = { ...taskDetails, startingProgressValue: inputValue };
                updateTask(newTask, false);
              }
            }}
            // onChange={(e, value) => {
            //   setTarget(value);
            // }}
          />
        </StyledGrid> */}
        {dueDate && (
          <ProgressGraph
            goalId={goalId}
            achieved={achieved}
            initialValue={startingProgressValue}
            targetValue={target}
            startDate={startDate}
            dueDate={dueDate}
            unit={measureUnit}
            // progressLevel is used for color.
            progressLevel={goalProgressLevel}
          />
        )}
      </BarAndGraphContainer>
    </div>
  );
});

const CustomTooltip = ({ payloadProp, unitSymbol, isPrefix, orgCurrency }) => {
  const ACHIEVED_DATAKEY = 'actual';
  const TARGET_DATAKEY = 'target';

  const { active, payload, label } = payloadProp;

  let achieved = null;
  let target = null;
  let updatedBy = null; // user info
  let progressLevel = null;
  let createdAt = null;

  if (payload.length > 0) {
    payload.forEach((line) => {
      if (line.dataKey === ACHIEVED_DATAKEY) {
        achieved = line.value;
        updatedBy = line.payload.updatedBy;
        progressLevel = line.payload.progressLevel;
        createdAt = line.payload.createdAt;
      }
      if (line.dataKey === TARGET_DATAKEY) {
        target = line.value;
      }
    });
  }

  if (active) {
    return (
      <StyledPaper>
        {achieved !== null && (
          <SpaceBetweenContainer>
            <VerticalFlexContainer>
              <StyledTypography variant="h6" style={{ fontSize: '20px' }} customColor={theme.palette.text.black}>
                {isPrefix
                  ? `${unitSymbol}${numberFormatter(achieved, orgCurrency)}`
                  : `${numberFormatter(achieved, orgCurrency)}${unitSymbol}`}
              </StyledTypography>
              <Typography8 topGutter={1}>Achieved</Typography8>
            </VerticalFlexContainer>
            <div>
              {progressLevel ? (
                <Typography4 customColor={GOAL_PROGRESS_OPTIONS_MAP[progressLevel].color} leftGutter={5}>
                  {GOAL_PROGRESS_OPTIONS_MAP[progressLevel].label}
                </Typography4>
              ) : null}
            </div>
          </SpaceBetweenContainer>
        )}
        {target !== null && (
          <SpaceBetweenContainer topGutter={3}>
            <VerticalFlexContainer>
              <StyledTypography variant="h6" style={{ fontSize: '20px' }} customColor={theme.palette.text.black}>
                {isPrefix
                  ? `${unitSymbol}${numberFormatter(target, orgCurrency)}`
                  : `${numberFormatter(target, orgCurrency)}${unitSymbol}`}
              </StyledTypography>
              <Typography8 topGutter={1}>Expected</Typography8>
            </VerticalFlexContainer>
            <div style={{ marginLeft: '40px', paddingTop: '5px' }}>
              <DueDateField dueDate={getDateFromEpoch(createdAt)} color={theme.palette.grey.main} />
            </div>
            {/* <StyledTypography leftGutter={2} variant="body1">
              {convertDateToString4(getDateFromEpoch(label))}
            </StyledTypography> */}
          </SpaceBetweenContainer>
        )}
        {updatedBy && (
          <>
            <Divider1 topGutter={2} bottomGutter={2} />
            <VerticallyAlignedFlexContainer>
              <StyledAvatar size="fixed24px" border="1px solid #EAEAEA" src={updatedBy.avatar} />
              <Typography4 leftGutter={1.5}>{updatedBy.name}</Typography4>
            </VerticallyAlignedFlexContainer>
          </>
        )}
      </StyledPaper>
    );
  }

  return null;
};

function getTargetEndpoints(initialValue, startDate, targetValue, dueDate) {
  const slope = (targetValue - initialValue) / differenceInCalendarDays(dueDate, startDate);

  return [
    {
      createdAt: convertDateToString3(startDate),
      target: initialValue,
    },
    {
      createdAt: convertDateToString3(dueDate),
      target: targetValue,
    },
  ];
}

function addPointsToGraphData(data, type = null) {
  // granularity would be day.
  // ignoring the case when progress is updated after due date.
  // ignoring if slope is infinity or 0.
  const mappedData = data.map((dataObj) => {
    if (type === GOAL_PROGRESS_ENTITIES.ACHIEVEDLIST) {
      delete dataObj.target;
      return {
        ...dataObj,
        createdAt: endOfDay(dataObj.createdAt).valueOf(),
        isAchievedValue: true,
      };
    }
    return {
      createdAt: endOfDay(dataObj.createdAt).valueOf(),
      target: dataObj.target,
      isTargetValue: true,
    };
  });

  return [...mappedData].sort((pointA, pointB) => (pointA.createdAt <= pointB.createdAt ? -1 : 1));
}

const findIndexOfTargetForDate = (date, targetMappedData) =>
  targetMappedData.findIndex((target) => target.createdAt === date);

const findSlopeBetweenPoints = ({ initialPoint, finalPoint, startDate, endDate }) => {
  const slope = ((finalPoint - initialPoint) / differenceInCalendarDays(endDate, startDate)).toFixed(3);
  return slope;
};

const indexOfClosestValueToNumber = (el, arr, start, end) => {
  // finding the index where the el would've been inserted with sorting maintained
  start = start || 0;
  end = end || arr.length;
  const pivot = parseInt(start + (end - start) / 2, 10);
  if (end - start <= 1 || arr[pivot] === el) return pivot;
  if (arr[pivot] < el) {
    return indexOfClosestValueToNumber(el, arr, pivot, end);
  }
  return indexOfClosestValueToNumber(el, arr, start, pivot);
};

const targetAtAchievedPoint = (targetMappedData, achievedMappedData) => {
  const targetMappedDataTS = targetMappedData.map((target) => target.createdAt);
  const achievedMappedDataWithTargets = achievedMappedData.map((achieved) => {
    if (findIndexOfTargetForDate(achieved.createdAt, targetMappedData) < 0) {
      const indexOfClosestValue = indexOfClosestValueToNumber(achieved.createdAt, targetMappedDataTS);

      if (!targetMappedData[indexOfClosestValue + 1]) {
        // if achieved point lies after the due date
        return {
          ...achieved,
          target: targetMappedData[indexOfClosestValue].target,
        };
      }

      const slope = findSlopeBetweenPoints({
        initialPoint: targetMappedData[indexOfClosestValue].target,
        finalPoint: targetMappedData[indexOfClosestValue + 1].target,
        startDate: targetMappedData[indexOfClosestValue].createdAt,
        endDate: targetMappedData[indexOfClosestValue + 1].createdAt,
      });

      const days = differenceInCalendarDays(
        getDateFromEpoch(achieved.createdAt),
        getDateFromEpoch(targetMappedData[indexOfClosestValue].createdAt)
      );
      return {
        ...achieved,
        target: (slope * days + targetMappedData[indexOfClosestValue].target).toFixed(2),
      };
    }
    // if the targetvalue already exists for the achieved point
    // i.e. user updated achieved on the right day
    return {
      ...achieved,
      ...targetMappedData[findIndexOfTargetForDate(achieved.createdAt, targetMappedData)],
    };
  });

  return actualAtTargetPoints([...targetMappedData, ...achievedMappedDataWithTargets]);
};

const getMappedDataWithoutDuplicates = (array) => {
  const graphDataMap = new Map();
  array.forEach((data) => graphDataMap.set(data.createdAt, data));
  let dataWithoutDuplicates = [];
  // creating a map, so that if there are 2 or more values of same day then they'll get merged
  for (const value of graphDataMap.values()) {
    dataWithoutDuplicates.push(value);
  }
  dataWithoutDuplicates = dataWithoutDuplicates.sort((pointA, pointB) =>
    pointA.createdAt <= pointB.createdAt ? -1 : 1
  );
  return dataWithoutDuplicates;
};

const getArrayWithActualValues = (array) => {
  // finding index of today (Date.now()) in the array of graph data
  const arrayWithActualValues = [...array];
  const endOfToday = endOfDay(Date.now()).valueOf();
  const arrayTS = arrayWithActualValues.map((el) => el.createdAt);
  const index = indexOfClosestValueToNumber(endOfToday, arrayTS);

  /* 
    recursive calls will start at index of today (Date.now()) and
    go till first element in the array of graph data 
    and fill all the elements which didn't have an actual value 
    with an actual value
  */

  for (let i = index; i >= 0; --i) {
    if (!arrayWithActualValues[i].actual) {
      arrayWithActualValues[i].actual = getActualValue(arrayWithActualValues, i);
    }
  }
  return arrayWithActualValues;
};

const actualAtTargetPoints = (dataArray) => {
  const dataWithoutDuplicates = getMappedDataWithoutDuplicates(dataArray);
  // adding actual (achieved) value for the targets that were missed by the user
  const mappedData = getArrayWithActualValues(dataWithoutDuplicates);
  return mappedData;
};

const getActualValue = (array, start) => {
  // recursively get actual value
  // by going to previous element in array which had an actual value
  // base case will return 0 as actual value
  if (start === 0) {
    return array[0].target ?? 0;
  }
  if (array[start].actual) {
    return array[start].actual;
  }
  return getActualValue(array, start - 1);
};

export function ProgressGraph({
  goalId,
  achieved,
  initialValue = 0,
  targetValue,
  startDate,
  dueDate = 10,
  unit,
  progressLevel, // this is used for color only for now.
  goalClosureStatus = null,
  hideAxis = false,
}) {
  const [data, setData] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  let orgCurrency = loggedInEmployee?.organization?.currency ?? null;
  if (!orgCurrency) {
    orgCurrency = 'INR';
  }

  const { symbol: unitSymbol, isPrefix } = useMemo(() => getMeasureUnitDetails(unit, orgCurrency), [orgCurrency, unit]);

  useEffect(() => {
    setLoading(true);
    getGoalGraphProgress(goalId)
      .then((res) => ({
        achievedList: parseProgressGraphRes(res[GOAL_PROGRESS_ENTITIES.ACHIEVEDLIST]),
        targetList: parseProgressGraphRes(res[GOAL_PROGRESS_ENTITIES.TARGETLIST]),
      }))
      .then((data) => {
        const targetMappedData = addPointsToGraphData(
          data[GOAL_PROGRESS_ENTITIES.TARGETLIST],
          GOAL_PROGRESS_ENTITIES.TARGETLIST
        );
        const achievedMappedData = addPointsToGraphData(
          data[GOAL_PROGRESS_ENTITIES.ACHIEVEDLIST],
          GOAL_PROGRESS_ENTITIES.ACHIEVEDLIST
        );
        const mappedData = targetAtAchievedPoint(targetMappedData, achievedMappedData);
        setData(mappedData);
      })
      .catch((err) => {
        console.error(err || 'Error occured while fetch graph data points');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [goalId, achieved, initialValue, targetValue, dueDate]);

  // const data = [
  //   {
  //     actual: 5,
  //     createdDate: convertDateToString5(new Date(1604480139848))
  //   },
  // ];

  const salt = generateKey(3);
  const colorAreaChartId = `colorAreaChart-${goalId}${salt}`;

  return (
    <>
      {isLoading ? (
        <SkeletonLoader skeletons={['95%', '95%', '95%']} />
      ) : data.length > 0 ? (
        <ResponsiveContainer debounce={300} width="100%" aspect={2.5}>
          <AreaChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
            <defs>
              <linearGradient id={colorAreaChartId} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={GOAL_PROGRESS_OPTIONS_MAP[goalClosureStatus || progressLevel]?.tagColor}
                  stopOpacity={1}
                />
                <stop
                  offset="95%"
                  stopColor={GOAL_PROGRESS_OPTIONS_MAP[goalClosureStatus || progressLevel]?.tagColor}
                  stopOpacity={0.9}
                />
              </linearGradient>
            </defs>
            {!hideAxis && (
              <XAxis
                hide
                width={2}
                stroke="lightgrey"
                tick={false}
                dataKey="createdAt"
                type="number"
                domain={['dataMin', 'dataMax']}
                tickFormatter={(value) => convertDateToString3(getDateFromEpoch(value))}
              />
            )}
            <ReferenceLine y={0} stroke="lightgrey" width={2} />
            {!hideAxis && <YAxis width={2} stroke="lightgrey" tick={false} />}
            <Tooltip
              allowEscapeViewBox
              content={(payloadProp) => (
                <CustomTooltip
                  payloadProp={payloadProp}
                  unitSymbol={unitSymbol}
                  isPrefix={isPrefix}
                  orgCurrency={orgCurrency}
                />
              )}
            />
            <Area
              // dot={<circle fill='red' radius={2} strokeWidth={1}></circle>}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
              name="Achieved"
              type="monotone"
              dataKey="actual"
              stroke={`url(#${colorAreaChartId})`}
              connectNulls
              fill={`url(#${colorAreaChartId})`}
            />
            <Area
              strokeDasharray="2 2"
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
              dataKey="target"
              // type="monotone"
              stroke="lightgrey"
              fillOpacity="0"
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : null}
    </>
  );
}

const NumberTextField = styled(StyledTextField)`
  .MuiInputBase-root {
    font-size: ${(props) => props.theme.typography.body2.fontSize};
  }
  ${({ error }) =>
    error &&
    css`
      fieldset {
        border-width: 1px !important;
        border-color: ${(props) => props.theme.palette.error.main} !important;
      }
    `}
  ${({ error }) => css`
    fieldset {
      border-width: 0px;
    }
    .Mui-focused fieldset,
    &:hover fieldset {
      border-width: 1px;
      border-color: ${error ? theme.palette.error.main : theme.palette.borders.lightGrey}!important;
    }
    .Mui-focused .MuiInputAdornment-positionEnd {
      visibility: hidden;
    }
  `}
  input {
    ${hideNumberInputArrowsCSS};
    padding: 0.5rem 0.75rem;
  }
  width: 50%;
  // margin-left: -0.5rem;
`;

const ProgressBarContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 1.5rem;
`;

const BarAndGraphContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

// const StyledTextField = styled(TextField)`
//   input {
//     padding: 0.5rem;
//   }
// `;

const StyledTextFieldContainer = styled.div`
  margin-left: ${(props) => props.theme.spacing(0.5)}rem;
  margin-right: ${(props) => props.theme.spacing(0.5)}rem;
`;

const StyledNumberTextField = styled(NumberTextField)`
  width: ${(props) => (props.$isPrefix === false ? '30%' : '100%')};
  ${(props) => (props.disabled ? `pointer-events: none` : '')};
  .MuiInputBase-root.Mui-disabled {
    color: ${theme.palette.text.primary};
  }
  input {
    font-size: 16px;
    font-weight: 700;
    ${(props) =>
      props.$isPrefix &&
      css`
        padding-left: 0;
      `}
  }
`;

const StyledPaper = styled(Paper)`
  padding: 16px;
  padding-bottom: 18px;
  border: 1px solid ${(props) => props.theme.palette.borders.lighterGrey};
  box-shadow: 3px 4px 5px 0px #0000001c;
`;

const StyledInputAdornment = styled(InputAdornment)`
  p {
    color: ${theme.palette.text.primary};
  }
`;

export default GoalProgress;
